import { Link } from '@inertiajs/react'
import { AlertTriangle, ArrowLeft } from 'lucide-react'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'
import { IncidentSignals, RefLine } from './IncidentSignals'
import { IncidentTimeline } from './IncidentTimeline'
import { formatIncidentDate } from './incidentFormatting'
import type { IncidentDetail, IncidentTimelineEvent } from './types'

interface Props {
  incident: IncidentDetail
  timeline: IncidentTimelineEvent[]
}

const SEVERITY_LABELS: Record<string, string> = { critical: 'Crítico', high: 'Alto', medium: 'Médio', ignored: 'Ignorado' }
const STATUS_LABELS: Record<string, string> = { open: 'Aberto', acknowledged: 'Reconhecido', resolved: 'Resolvido', suppressed: 'Suprimido' }

function Show({ incident, timeline }: Props) {
  const dryRunText = typeof incident.slackDryRunPayload.text === 'string' ? incident.slackDryRunPayload.text : null
  const decisionStatus = typeof incident.slackAlertDecision.status === 'string' ? incident.slackAlertDecision.status : incident.slackAlertStatus

  return (
    <div>
      <div className="mb-4">
        <Link href="/cc/incidents" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Incidentes
        </Link>
      </div>

      <div className="flex items-start gap-3 mb-6">
        <AlertTriangle className="h-6 w-6 text-foreground shrink-0 mt-1" />
        <div className="min-w-0">
          <h2 className="text-2xl font-bold text-foreground">{incident.title}</h2>
          <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground flex-wrap">
            <span>{SEVERITY_LABELS[incident.severity]}</span>
            <span>·</span>
            <span>{STATUS_LABELS[incident.status]}</span>
            <span>·</span>
            <span>{incident.occurrenceCount} ocorrência(s)</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-6">
        <main className="space-y-6 min-w-0">
          <section>
            <h3 className="text-sm font-semibold text-foreground mb-2">Resumo</h3>
            <div className="bg-card border border-border rounded-lg p-4 space-y-3">
              <p className="text-sm text-foreground">{incident.impact || 'Impacto operacional não informado.'}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Instâncias:</span> {incident.affectedInstances.join(', ') || '—'}</div>
                <div><span className="text-muted-foreground">Fontes:</span> {incident.correlatedSources.join(', ') || incident.primarySource}</div>
                <div><span className="text-muted-foreground">Primeira:</span> {formatIncidentDate(incident.firstSeenAt)}</div>
                <div><span className="text-muted-foreground">Última:</span> {formatIncidentDate(incident.lastSeenAt)}</div>
              </div>
            </div>
          </section>

          <IncidentTimeline timeline={timeline} />
        </main>

        <aside className="space-y-6">
          <IncidentSignals incident={incident} />

          <section>
            <h3 className="text-sm font-semibold text-foreground mb-2">Referências</h3>
            <dl className="bg-card border border-border rounded-lg px-4">
              {incident.operatorNavigationRefs.map((item) => <RefLine key={item.kind} item={item} />)}
              {incident.operatorNavigationRefs.length === 0 && <div className="py-3 text-sm text-muted-foreground">Nenhuma referência navegável registrada.</div>}
              <RefLine item={{ kind: 'fingerprint', label: 'Fingerprint', value: incident.fingerprint, href: null, external: false }} />
              <RefLine item={{ kind: 'suppression', label: 'Suppression', value: incident.suppressionKey, href: null, external: false }} />
            </dl>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-foreground mb-2">Slack dry-run</h3>
            <div className="bg-card border border-border rounded-lg p-4 space-y-3">
              <div className="text-sm"><span className="text-muted-foreground">Status:</span> {decisionStatus || 'não registrado'}</div>
              <div className="text-sm"><span className="text-muted-foreground">Ref:</span> {incident.slackDryRunRef || '—'}</div>
              <pre className="text-xs bg-muted rounded-md p-3 overflow-auto whitespace-pre-wrap max-h-72">{dryRunText || 'Payload dry-run indisponível.'}</pre>
            </div>
          </section>
        </aside>
      </div>
    </div>
  )
}

Show.layout = (page: React.ReactElement) => <CommandCenterLayout>{page}</CommandCenterLayout>

export default Show
